
import { getComponentProperties, arrayIOProperties } from "./Symbol.js";
import { w_, h_, PortType, Edge, outputPorts_, inputPorts_, Port, SymbolInfo, type_, shifterWidthFor, ComponentType, Component as Component_1 } from "../Common/CommonTypes.js";
import { DrawModelType_SymbolT_PortMaps, DrawModelType_SymbolT_portMaps_, DrawModelType_SymbolT_component_, DrawModelType_SymbolT_Symbol } from "../Model/DrawModelType.js";
import { printf, toFail } from "../fable_modules/fable-library-js.5.13.0/String.js";
import { remove, add, find } from "../fable_modules/fable-library-js.5.13.0/Map.js";
import { Optic_Map, Optic_Map_op_HatPercent_Z1462312A, Optic_Set, Optic_Set_op_HatEquals_Z147477F8, Compose_Lens, Compose_Lens_op_GreaterMinusGreater_Z15C92E89 } from "../Common/Optics.js";
import { toInt32 } from "../fable_modules/fable-library-js.5.13.0/BigInt.js";
import { IdAllocator_newPortId } from "../Common/Helpers.js";
import { defaultArg, value as value_3 } from "../fable_modules/fable-library-js.5.13.0/Option.js";
import { ofArray, item, empty, reverse, fold, filter, insertAt, tryItem, length, map, singleton, getSlice, append } from "../fable_modules/fable-library-js.5.13.0/List.js";
import { equals } from "../fable_modules/fable-library-js.5.13.0/Util.js";
import { toList } from "../fable_modules/fable-library-js.5.13.0/Seq.js";
import { rangeDouble } from "../fable_modules/fable-library-js.5.13.0/Range.js";

/**
 * Helper function to change the number of bits expected in a port of each component type.
 * A symbol whose width is worked out from the text it draws, redone after that text has changed.
 * 
 * Only the IO of an array component needs it: its legend says its own name, its channel and its
 * width - "Join[3].(7:0)" - so changing any of those changes how wide the symbol has to be, and
 * nothing else in Issie has a size that depends on a field the Properties pane can edit.
 * The symbol resized to fit the legend its CURRENT type gives it.
 * 
 * Array IO carries its width in its legend rather than in a separate line of text, so changing a
 * width changes how much text there is to fit and the symbol has to follow. Public so that a test
 * can ask whether it does: the resize is invisible from the type alone, and a symbol whose text
 * has outgrown it is exactly what nobody notices until they see it drawn.
 */
export function resizedForLegend(symbol) {
    let bind$0040;
    const matchValue = symbol.Component.Type;
    switch (matchValue.tag) {
        case 50:
        case 51:
        case 52:
        case 53: {
            const patternInput = arrayIOProperties(symbol.ArrayText, symbol.Component.Type);
            return new DrawModelType_SymbolT_Symbol(symbol.Pos, symbol.CentrePos, symbol.OffsetFromBBCentre, symbol.InWidth0, symbol.InWidth1, symbol.InWidths, symbol.LabelBoundingBox, symbol.LabelHasDefaultPos, symbol.LabelRotation, symbol.Appearance, symbol.Id, (bind$0040 = symbol.Component, new Component_1(bind$0040.Id, bind$0040.Type, bind$0040.Label, bind$0040.InputPorts, bind$0040.OutputPorts, bind$0040.X, bind$0040.Y, patternInput[2], patternInput[3], bind$0040.SymbolInfo, bind$0040.SlotInfo)), symbol.Annotation, symbol.Moving, symbol.IsClocked, symbol.STransform, symbol.ReversedInputPorts, symbol.PortMaps, symbol.HScale, symbol.VScale, symbol.MovingPort, symbol.MovingPortTarget, symbol.ArrayText);
        }
        default:
            return symbol;
    }
}

/**
 * The component type with a different bus width, for every component that has one.
 * 
 * Pure, and separate from the symbol it is applied to, so that "does a width change reach this
 * component type" can be asked without a draw block model. It is worth asking: this match ends in
 * a wildcard that returns the type UNCHANGED, so a component missing from it accepts a width typed
 * into Properties and then silently throws it away - which is how the array IO components behaved
 * until they were added below.
 */
export function withNumberOfBits(newBits, compType) {
    switch (compType.tag) {
        case 48:
            return toFail(printf("Legacy Input component types should never occur"));
        case 0:
            return new ComponentType(/* Input1 */ 0, [newBits, compType.fields[1]]);
        case 1:
            return new ComponentType(/* Output */ 1, [newBits]);
        case 2:
            return new ComponentType(/* Viewer */ 2, [newBits]);
        case 17:
            return new ComponentType(/* NbitsAdder */ 17, [newBits]);
        case 18:
            return new ComponentType(/* NbitsAdderNoCin */ 18, [newBits]);
        case 20:
            return new ComponentType(/* NbitsAdderNoCinCout */ 20, [newBits]);
        case 19:
            return new ComponentType(/* NbitsAdderNoCout */ 19, [newBits]);
        case 21:
            return new ComponentType(/* NbitsXor */ 21, [newBits, compType.fields[1]]);
        case 22:
            return new ComponentType(/* NbitsAnd */ 22, [newBits]);
        case 24:
            return new ComponentType(/* NbitsOr */ 24, [newBits]);
        case 23:
            return new ComponentType(/* NbitsNot */ 23, [newBits]);
        case 25:
            return new ComponentType(/* NbitSpreader */ 25, [newBits]);
        case 33:
            return new ComponentType(/* Register */ 33, [newBits]);
        case 34:
            return new ComponentType(/* RegisterE */ 34, [newBits]);
        case 35:
            return new ComponentType(/* Counter */ 35, [newBits]);
        case 37:
            return new ComponentType(/* CounterNoEnable */ 37, [newBits]);
        case 36:
            return new ComponentType(/* CounterNoLoad */ 36, [newBits]);
        case 38:
            return new ComponentType(/* CounterNoEnableLoad */ 38, [newBits]);
        case 28:
            return new ComponentType(/* SplitWire */ 28, [newBits]);
        case 6:
            return new ComponentType(/* BusSelection */ 6, [newBits, compType.fields[1]]);
        case 47:
            return new ComponentType(/* BusCompare */ 47, [newBits, compType.fields[1]]);
        case 5:
            return new ComponentType(/* BusCompare1 */ 5, [newBits, compType.fields[1], compType.fields[2]]);
        case 7:
            return new ComponentType(/* Constant1 */ 7, [newBits, compType.fields[1], compType.fields[2]]);
        case 46:
            return new ComponentType(/* Shift */ 46, [newBits, shifterWidthFor(newBits), compType.fields[2]]);
        case 50:
            return new ComponentType(/* BusOut */ 50, [newBits]);
        case 51:
            return new ComponentType(/* MuxOut */ 51, [newBits]);
        case 52:
            return new ComponentType(/* JoinOut */ 52, [newBits, compType.fields[1]]);
        case 53:
            return new ComponentType(/* JoinIn */ 53, [newBits, compType.fields[1]]);
        default:
            return compType;
    }
}

export function changeNumberOfBitsf(symModel, compId, newBits) {
    let optic, value;
    const symbol = find(compId, symModel.Symbols);
    return resizedForLegend(((optic = Compose_Lens_op_GreaterMinusGreater_Z15C92E89(Compose_Lens.Lens, type_)(DrawModelType_SymbolT_component_), (value = withNumberOfBits(newBits, symbol.Component.Type), Optic_Set_op_HatEquals_Z147477F8(Optic_Set.Set$, optic)(value))))(symbol));
}

/**
 * Helper function to change the number of bits expected in the LSB port of BusSelection and BusCompare
 */
export function changeLsbf(symModel, compId, newLsb) {
    const symbol = find(compId, symModel.Symbols);
    let newcompotype;
    const matchValue = symbol.Component.Type;
    newcompotype = ((matchValue.tag === 6) ? (new ComponentType(/* BusSelection */ 6, [matchValue.fields[0], ~~toInt32(newLsb)])) : ((matchValue.tag === 47) ? (new ComponentType(/* BusCompare */ 47, [matchValue.fields[0], newLsb])) : ((matchValue.tag === 7) ? (new ComponentType(/* Constant1 */ 7, [matchValue.fields[0], newLsb, matchValue.fields[2]])) : toFail(printf("this shouldnt happen, incorrect call of message changeLsb")))));
    return Optic_Set_op_HatEquals_Z147477F8(Optic_Set.Set$, Compose_Lens_op_GreaterMinusGreater_Z15C92E89(Compose_Lens.Lens, type_)(DrawModelType_SymbolT_component_))(newcompotype)(symbol);
}

/**
 * Set which channel a join uses. The width is left alone: a join carries both, and the properties
 * pane edits them in two boxes.
 */
export function changeJoinNum(symModel, compId, newNum) {
    const symbol = find(compId, symModel.Symbols);
    let newcompotype;
    const matchValue = symbol.Component.Type;
    newcompotype = ((matchValue.tag === 52) ? (new ComponentType(/* JoinOut */ 52, [matchValue.fields[0], newNum])) : ((matchValue.tag === 53) ? (new ComponentType(/* JoinIn */ 53, [matchValue.fields[0], newNum])) : toFail(`changeJoinNum should only be called for a join, not ${matchValue}`)));
    return resizedForLegend(Optic_Set_op_HatEquals_Z147477F8(Optic_Set.Set$, Compose_Lens_op_GreaterMinusGreater_Z15C92E89(Compose_Lens.Lens, type_)(DrawModelType_SymbolT_component_))(newcompotype)(symbol));
}

/**
 * This function should be called for Input1 components only. Sets the default
 * value to be used in simulations for an Input1 component if it is not driven.
 */
export function changeInputValue(symModel, compId, newVal) {
    const symbol = find(compId, symModel.Symbols);
    let width_1;
    const matchValue = symbol.Component.Type;
    width_1 = ((matchValue.tag === 0) ? matchValue.fields[0] : toFail(printf("changeInputValue should only be called for Input components")));
    return Optic_Set_op_HatEquals_Z147477F8(Optic_Set.Set$, Compose_Lens_op_GreaterMinusGreater_Z15C92E89(Compose_Lens.Lens, type_)(DrawModelType_SymbolT_component_))(new ComponentType(/* Input1 */ 0, [width_1, newVal]))(symbol);
}

/**
 * Updates the value of a constant1 component and returns the updated symbol
 */
export function changeConstantf(symModel, compId, constantVal, constantText) {
    const symbol = find(compId, symModel.Symbols);
    let newcompotype;
    const matchValue = symbol.Component.Type;
    newcompotype = ((matchValue.tag === 7) ? (new ComponentType(/* Constant1 */ 7, [matchValue.fields[0], constantVal, constantText])) : toFail(printf("this shouldnt happen, incorrect call of message changeLsb")));
    return Optic_Set_op_HatEquals_Z147477F8(Optic_Set.Set$, Compose_Lens_op_GreaterMinusGreater_Z15C92E89(Compose_Lens.Lens, type_)(DrawModelType_SymbolT_component_))(newcompotype)(symbol);
}

/**
 * Updates the value of a busCompare1 component and returns the updated symbol
 */
export function changeBusComparef(symModel, compId, constantVal, constantText) {
    const symbol = find(compId, symModel.Symbols);
    let newcompotype;
    const matchValue = symbol.Component.Type;
    newcompotype = ((matchValue.tag === 5) ? (new ComponentType(/* BusCompare1 */ 5, [matchValue.fields[0], constantVal, constantText])) : toFail(printf("this shouldnt happen, incorrect call of message changeLsb")));
    return Optic_Set_op_HatEquals_Z147477F8(Optic_Set.Set$, Compose_Lens_op_GreaterMinusGreater_Z15C92E89(Compose_Lens.Lens, type_)(DrawModelType_SymbolT_component_))(newcompotype)(symbol);
}

export function changeReversedInputs(symModel, compId) {
    let bind$0040, matchValue_1, si;
    const symbol = find(compId, symModel.Symbols);
    let newValue;
    const matchValue = symbol.ReversedInputPorts;
    newValue = ((matchValue == null) ? true : (!matchValue));
    return new DrawModelType_SymbolT_Symbol(symbol.Pos, symbol.CentrePos, symbol.OffsetFromBBCentre, symbol.InWidth0, symbol.InWidth1, symbol.InWidths, symbol.LabelBoundingBox, symbol.LabelHasDefaultPos, symbol.LabelRotation, symbol.Appearance, symbol.Id, (bind$0040 = symbol.Component, new Component_1(bind$0040.Id, bind$0040.Type, bind$0040.Label, bind$0040.InputPorts, bind$0040.OutputPorts, bind$0040.X, bind$0040.Y, bind$0040.H, bind$0040.W, (matchValue_1 = symbol.Component.SymbolInfo, (matchValue_1 == null) ? undefined : ((si = matchValue_1, new SymbolInfo(si.LabelBoundingBox, si.LabelRotation, si.STransform, newValue, si.PortOrientation, si.PortOrder, si.HScale, si.VScale)))), bind$0040.SlotInfo)), symbol.Annotation, symbol.Moving, symbol.IsClocked, symbol.STransform, newValue, symbol.PortMaps, symbol.HScale, symbol.VScale, symbol.MovingPort, symbol.MovingPortTarget, symbol.ArrayText);
}

export function createNewPort(no, hostID, portType) {
    return new Port(IdAllocator_newPortId(), no, portType, hostID);
}

export function updateSymPortMaps(newPortMaps, newInputPorts, newOutputPorts, sym) {
    let f, f1, f2;
    return ((f = ((f1 = Optic_Set_op_HatEquals_Z147477F8(Optic_Set.Set$, inputPorts_)(newInputPorts), (f2 = Optic_Set_op_HatEquals_Z147477F8(Optic_Set.Set$, outputPorts_)(newOutputPorts), (arg_4) => f2(f1(arg_4))))), Optic_Map_op_HatPercent_Z1462312A(Optic_Map.Map$, DrawModelType_SymbolT_component_)(f)))(Optic_Set_op_HatEquals_Z147477F8(Optic_Set.Set$, DrawModelType_SymbolT_portMaps_)(newPortMaps)(sym));
}

export function portNoDown(port) {
    return new Port(port.Id, value_3(port.PortNumber) - 1, port.PortType, port.HostId);
}

export function portNoUp(port) {
    return new Port(port.Id, value_3(port.PortNumber) + 1, port.PortType, port.HostId);
}

/**
 * add the port specified by its type and number to the given symbol
 * if edgeOpt is None the port will be added on the same edge as the port
 * with the highest number
 */
export function addNumberPort(pType, pNum, sym, edgeOpt) {
    let edge, option_2, tupledArg, option_1, option, sym_1, order, newOrientation;
    const newPort = createNewPort(pNum, sym.Component.Id, pType);
    const patternInput = (pType.tag === 1) ? [sym.Component.InputPorts, append(getSlice(undefined, pNum - 1, sym.Component.OutputPorts), append(singleton(newPort), map(portNoUp, getSlice(pNum, undefined, sym.Component.OutputPorts))))] : [append(getSlice(undefined, pNum - 1, sym.Component.InputPorts), append(singleton(newPort), map(portNoUp, getSlice(pNum, undefined, sym.Component.InputPorts)))), sym.Component.OutputPorts];
    const insertIndex = (sym.STransform.flipped ? ((pType.tag === 1) ? ((length(sym.Component.OutputPorts) - 1) - pNum) : ((length(sym.Component.InputPorts) - 1) - pNum)) : ((pType.tag === 1) ? 0 : pNum)) | 0;
    return updateSymPortMaps((edge = ((option_2 = edgeOpt, (option_2 != null) ? option_2 : ((tupledArg = ((pType.tag === 1) ? [tryItem(length(sym.Component.OutputPorts) - 1, sym.Component.OutputPorts), Edge.Right] : [tryItem(length(sym.Component.InputPorts) - 1, sym.Component.InputPorts), Edge.Left]), defaultArg((option_1 = ((option = tupledArg[0], (option != null) ? option.Id : undefined)), (option_1 != null) ? find(option_1, sym.PortMaps.Orientation) : undefined), tupledArg[1]))))), (sym_1 = sym, (order = sym_1.PortMaps.Order, (newOrientation = add(newPort.Id, edge, sym_1.PortMaps.Orientation), new DrawModelType_SymbolT_PortMaps(add(edge, insertAt(insertIndex, newPort.Id, find(edge, order)), order), newOrientation))))), patternInput[0], patternInput[1], sym);
}

/**
 * remove the port specified by its type and number from the given symbol
 */
export function deleteNumberPort(pType, pNum, sym) {
    let option_3, option_1, option_5, pId, sym_1, orientation, order, edge, newOrientation;
    const patternInput = (pType.tag === 1) ? [(option_3 = tryItem(pNum, sym.Component.OutputPorts), (option_3 != null) ? option_3.Id : undefined), sym.Component.InputPorts, append(getSlice(undefined, pNum - 1, sym.Component.OutputPorts), map(portNoDown, getSlice(pNum + 1, undefined, sym.Component.OutputPorts)))] : [(option_1 = tryItem(pNum, sym.Component.InputPorts), (option_1 != null) ? option_1.Id : undefined), append(getSlice(undefined, pNum - 1, sym.Component.InputPorts), map(portNoDown, getSlice(pNum + 1, undefined, sym.Component.InputPorts))), sym.Component.OutputPorts];
    return updateSymPortMaps(defaultArg((option_5 = patternInput[0], (option_5 != null) ? ((pId = option_5, (sym_1 = sym, (orientation = sym_1.PortMaps.Orientation, (order = sym_1.PortMaps.Order, (edge = find(pId, orientation), (newOrientation = remove(pId, orientation), new DrawModelType_SymbolT_PortMaps(add(edge, filter((id) => !equals(id, pId), find(edge, order)), order), newOrientation)))))))) : undefined), sym.PortMaps), patternInput[1], patternInput[2], sym);
}

/**
 * add the ports specified by the type and the list of port numbers
 * if edgeOpt is None they will be added on the same edge as the port
 * with the highest number
 */
export function addPorts(pType, pNumList, edgeOpt, sym) {
    return fold((sym_1, pNum) => addNumberPort(pType, pNum, sym_1, edgeOpt), sym, pNumList);
}

/**
 * delete ports specified by the list of port numbers
 * must be in ascending order because otherwise wrong ports will be deleted
 */
export function deletePorts(pType, pNumList, sym) {
    return fold((sym_1, pNum) => deleteNumberPort(pType, pNum, sym_1), sym, reverse(pNumList));
}

/**
 * add and remove ports to obtain the given number of input and output ports
 * the ports will be added at the highest index, as well as removed from the end
 * of the port list
 */
export function varyNumberOfPorts(pType, numInPorts, numOutPorts, sym) {
    const comp = sym.Component;
    const matchValue = length(comp.InputPorts) | 0;
    const matchValue_1 = length(comp.OutputPorts) | 0;
    if ((numInPorts >= matchValue) && (numOutPorts >= matchValue_1)) {
        const sym_2 = addPorts(PortType.Input, toList(rangeDouble(matchValue, 1, numInPorts - 1)), undefined, sym);
        return addPorts(PortType.Output, toList(rangeDouble(matchValue_1, 1, numOutPorts - 1)), undefined, sym_2);
    }
    else if ((numInPorts >= matchValue) && (numOutPorts < matchValue_1)) {
        const sym_4 = addPorts(PortType.Input, toList(rangeDouble(matchValue, 1, numInPorts - 1)), undefined, sym);
        return deletePorts(PortType.Output, toList(rangeDouble(numOutPorts, 1, matchValue_1 - 1)), sym_4);
    }
    else if ((numInPorts < matchValue) && (numOutPorts >= matchValue_1)) {
        const sym_6 = deletePorts(PortType.Input, toList(rangeDouble(numInPorts, 1, matchValue - 1)), sym);
        return addPorts(PortType.Output, toList(rangeDouble(matchValue_1, 1, numOutPorts - 1)), undefined, sym_6);
    }
    else if ((numInPorts < matchValue) && (numOutPorts < matchValue_1)) {
        const sym_8 = deletePorts(PortType.Input, toList(rangeDouble(numInPorts, 1, matchValue - 1)), sym);
        return deletePorts(PortType.Output, toList(rangeDouble(numOutPorts, 1, matchValue_1 - 1)), sym_8);
    }
    else {
        return toFail(printf("new port counts can\'t be obtained"));
    }
}

export function changeAdderComponent(symModel, compId, oldComp, newCompType) {
    let f, rotation, flipped;
    const oldCompType = oldComp.Type;
    const symbol = find(compId, symModel.Symbols);
    const patternInput = (oldCompType.tag === 17) ? ((newCompType.tag === 18) ? [singleton(0), PortType.Input] : ((newCompType.tag === 19) ? [singleton(1), PortType.Output] : [empty(), PortType.Input])) : ((oldCompType.tag === 19) ? ((newCompType.tag === 20) ? [singleton(0), PortType.Input] : [empty(), PortType.Input]) : ((oldCompType.tag === 18) ? ((newCompType.tag === 20) ? [singleton(1), PortType.Output] : [empty(), PortType.Input]) : [empty(), PortType.Input]));
    const patternInput_1 = (oldCompType.tag === 18) ? ((newCompType.tag === 17) ? [singleton(0), PortType.Input] : [empty(), PortType.Input]) : ((oldCompType.tag === 20) ? ((newCompType.tag === 19) ? [singleton(0), PortType.Input] : ((newCompType.tag === 18) ? [singleton(1), PortType.Output] : [empty(), PortType.Input])) : ((oldCompType.tag === 19) ? ((newCompType.tag === 17) ? [singleton(1), PortType.Output] : [empty(), PortType.Input]) : [empty(), PortType.Input]));
    const addL = patternInput_1[0];
    return ((f = Optic_Set_op_HatEquals_Z147477F8(Optic_Set.Set$, type_)(newCompType), Optic_Map_op_HatPercent_Z1462312A(Optic_Map.Map$, DrawModelType_SymbolT_component_)(f)))(deletePorts(patternInput[1], patternInput[0], addPorts(patternInput_1[1], addL, !equals(addL, empty()) ? ((oldCompType.tag === 18) ? ((newCompType.tag === 17) ? ((rotation = symbol.STransform.Rotation, (flipped = symbol.STransform.flipped, (rotation.tag === 1) ? (flipped ? Edge.Left : Edge.Right) : ((rotation.tag === 3) ? (flipped ? Edge.Right : Edge.Left) : ((rotation.tag === 2) ? (flipped ? Edge.Top : Edge.Top) : (flipped ? Edge.Bottom : Edge.Bottom)))))) : toFail(printf("Can\'t happen"))) : ((oldCompType.tag === 20) ? ((newCompType.tag === 19) ? ((rotation = symbol.STransform.Rotation, (flipped = symbol.STransform.flipped, (rotation.tag === 1) ? (flipped ? Edge.Left : Edge.Right) : ((rotation.tag === 3) ? (flipped ? Edge.Right : Edge.Left) : ((rotation.tag === 2) ? (flipped ? Edge.Top : Edge.Top) : (flipped ? Edge.Bottom : Edge.Bottom)))))) : ((newCompType.tag === 18) ? find(item(0, oldComp.OutputPorts).Id, symbol.PortMaps.Orientation) : toFail(printf("Can\'t happen")))) : ((oldCompType.tag === 19) ? ((newCompType.tag === 17) ? find(item(0, oldComp.OutputPorts).Id, symbol.PortMaps.Orientation) : toFail(printf("Can\'t happen"))) : toFail(printf("Can\'t happen"))))) : undefined, symbol)));
}

export function changeCustom(symModel, compId, oldComp, newCompType) {
    let f;
    const symbol = find(compId, symModel.Symbols);
    return ((f = Optic_Set_op_HatEquals_Z147477F8(Optic_Set.Set$, type_)(newCompType), Optic_Map_op_HatPercent_Z1462312A(Optic_Map.Map$, DrawModelType_SymbolT_component_)(f)))(symbol);
}

export function changeCounterComponent(symModel, compId, oldComp, newCompType) {
    let f, f1_1, f1, f2, f2_1;
    const oldCompType = oldComp.Type;
    const symbol = find(compId, symModel.Symbols);
    let edge_1;
    const edge = find(item(0, oldComp.OutputPorts).Id, symbol.PortMaps.Orientation);
    edge_1 = ((edge.tag === 0) ? Edge.Bottom : ((edge.tag === 2) ? Edge.Right : ((edge.tag === 1) ? Edge.Top : Edge.Left)));
    const removeL = (oldCompType.tag === 35) ? ((newCompType.tag === 36) ? ofArray([0, 1]) : ((newCompType.tag === 37) ? singleton(2) : empty())) : ((oldCompType.tag === 37) ? ((newCompType.tag === 38) ? ofArray([0, 1]) : empty()) : ((oldCompType.tag === 36) ? ((newCompType.tag === 38) ? singleton(0) : empty()) : empty()));
    const addL = (oldCompType.tag === 36) ? ((newCompType.tag === 35) ? ofArray([0, 1]) : empty()) : ((oldCompType.tag === 38) ? ((newCompType.tag === 37) ? ofArray([0, 1]) : ((newCompType.tag === 36) ? singleton(0) : empty())) : ((oldCompType.tag === 37) ? ((newCompType.tag === 35) ? singleton(2) : empty()) : empty()));
    let patternInput;
    const matchValue_2 = getComponentProperties(newCompType, "");
    patternInput = [matchValue_2[2], matchValue_2[3]];
    return ((f = ((f1_1 = ((f1 = Optic_Set_op_HatEquals_Z147477F8(Optic_Set.Set$, type_)(newCompType), (f2 = Optic_Set_op_HatEquals_Z147477F8(Optic_Set.Set$, h_)(patternInput[0]), (arg_3) => f2(f1(arg_3))))), (f2_1 = Optic_Set_op_HatEquals_Z147477F8(Optic_Set.Set$, w_)(patternInput[1]), (arg_5) => f2_1(f1_1(arg_5))))), Optic_Map_op_HatPercent_Z1462312A(Optic_Map.Map$, DrawModelType_SymbolT_component_)(f)))(deletePorts(PortType.Input, removeL, addPorts(PortType.Input, addL, edge_1, symbol)));
}

export function changeGateComponent(symModel, compId, gateType, n) {
    let f, f1, f2, value_1;
    return ((f = ((f1 = Optic_Set_op_HatEquals_Z147477F8(Optic_Set.Set$, type_)(new ComponentType(/* GateN */ 10, [gateType, n])), (f2 = ((value_1 = (((1.5 * 30) * n) / 2), Optic_Set_op_HatEquals_Z147477F8(Optic_Set.Set$, h_)(value_1))), (arg_3) => f2(f1(arg_3))))), Optic_Map_op_HatPercent_Z1462312A(Optic_Map.Map$, DrawModelType_SymbolT_component_)(f)))(varyNumberOfPorts(PortType.Input, n, 1, find(compId, symModel.Symbols)));
}

export function changeMergeNComponent(symModel, compId, numInputs) {
    let f, f1, f2, value_1;
    const numInputsEdit = ((numInputs > 2) ? numInputs : 3) | 0;
    return ((f = ((f1 = Optic_Set_op_HatEquals_Z147477F8(Optic_Set.Set$, type_)(new ComponentType(/* MergeN */ 29, [numInputs])), (f2 = ((value_1 = (((2 * 30) * numInputsEdit) / 2), Optic_Set_op_HatEquals_Z147477F8(Optic_Set.Set$, h_)(value_1))), (arg_3) => f2(f1(arg_3))))), Optic_Map_op_HatPercent_Z1462312A(Optic_Map.Map$, DrawModelType_SymbolT_component_)(f)))(varyNumberOfPorts(PortType.Input, numInputs, 1, find(compId, symModel.Symbols)));
}

export function changeSplitNComponent(symModel, compId, numOutputs, widths, lsbs) {
    let f, f1, f2, value_1;
    const numOutputsEdit = ((numOutputs > 2) ? numOutputs : 3) | 0;
    return ((f = ((f1 = Optic_Set_op_HatEquals_Z147477F8(Optic_Set.Set$, type_)(new ComponentType(/* SplitN */ 30, [numOutputs, widths, lsbs])), (f2 = ((value_1 = (((2 * 30) * numOutputsEdit) / 2), Optic_Set_op_HatEquals_Z147477F8(Optic_Set.Set$, h_)(value_1))), (arg_3) => f2(f1(arg_3))))), Optic_Map_op_HatPercent_Z1462312A(Optic_Map.Map$, DrawModelType_SymbolT_component_)(f)))(varyNumberOfPorts(PortType.Output, 1, numOutputs, find(compId, symModel.Symbols)));
}

